import './ImageDropperCropper.css';
import Dropzone from 'react-dropzone';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import NeddieButton from '../../../components/neddieButton';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { getOriginalFileURLFromCroppedURL } from '../../../modules/file/file.utils';
import { IOnImageChangeEvent, IOnImageDeleteEvent } from '../../../types/shared.types';

interface Crop {
  unit: '%' | 'px' | undefined;
  width: number;
  height?: number;
  x?: number;
  y?: number;
  aspect: number;
}

const defaultCrop: Crop = {
  unit: '%',
  width: 100,
  aspect: 16 / 9
};

const squareCrop: Crop = {
  unit: '%',
  width: 100,
  aspect: 1 / 1
};

const spotlightCrop: Crop = {
  unit: '%',
  width: 100,
  aspect: 3 / 2
};

const spotlightLogoCrop: Crop = {
  unit: '%',
  width: 100,
  aspect: 9 / 9
};

type UploadStatus = 'idle' | 'processing' | 'uploading' | 'success' | 'error';

interface IImageDropper {
  aspect: 'defaultCrop' | 'squareCrop' | 'spotlightCrop' | 'spotlightLogoCrop';
  defaultThumbNail?: string; //you can customize the drop zone when implementing the component. leave it empty string for default
  field: string;
  fileName: string;
  identifier?: string;
  onImageChange: (event: IOnImageChangeEvent) => void | Promise<unknown>;
  onImageDelete: (event: IOnImageDeleteEvent) => void | Promise<unknown>;
  /** Bubbles up whenever this cropper starts/stops an upload so parents can disable their Save button. */
  onUploadStatusChange?: (isUploading: boolean) => void;
}

/**
 * A centered crop that fits the given aspect ratio inside the displayed image,
 * expressed in % so it is resolution-independent. Guarantees a valid
 * completedCrop even if the user never drags.
 */
function centeredAspectCrop(
  displayWidth: number,
  displayHeight: number,
  aspect: number,
): Crop {
  let widthPct = 100;
  let heightPct = ((displayWidth / aspect) / displayHeight) * 100;

  if (heightPct > 100) {
    heightPct = 100;
    widthPct = ((displayHeight * aspect) / displayWidth) * 100;
  }

  return {
    unit: '%',
    width: widthPct,
    height: heightPct,
    x: (100 - widthPct) / 2,
    y: (100 - heightPct) / 2,
    aspect,
  };
}

/** Read a Blob/File as a base64 data URL (same-origin, so it never taints a canvas). */
function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Convert a react-image-crop crop (may be in % and may be missing x/y/height) to pixel values on the source image. */
function getPixelCrop(image: HTMLImageElement, crop: Crop) {
  const nw = image.naturalWidth;
  const nh = image.naturalHeight;

  let wPx = ((crop.width ?? 100) / 100) * nw;
  let hPx: number;
  if (crop.height != null) {
    hPx = (crop.height / 100) * nh;
  } else if (crop.aspect) {
    hPx = wPx / crop.aspect;
  } else {
    hPx = nh;
  }

  let xPx = ((crop.x ?? 0) / 100) * nw;
  let yPx = ((crop.y ?? 0) / 100) * nh;

  // Clamp so we never read outside the image bounds.
  wPx = Math.min(wPx, nw);
  hPx = Math.min(hPx, nh);
  xPx = Math.max(0, Math.min(xPx, nw - wPx));
  yPx = Math.max(0, Math.min(yPx, nh - hPx));

  return { xPx, yPx, wPx, hPx };
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | undefined> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b || undefined), 'image/jpeg', quality);
    } catch {
      // SecurityError => the source image tainted the canvas (cross-origin without CORS).
      resolve(undefined);
    }
  });
}

/**
 * Produce the cropped JPEG using the browser's native canvas instead of Jimp.
 * Drawing the <img> element applies EXIF orientation in modern browsers, so
 * phone photos are no longer saved sideways.
 */
async function getCroppedImg(image: HTMLImageElement, crop: Crop): Promise<Blob | undefined> {
  const { xPx, yPx, wPx, hPx } = getPixelCrop(image, crop);
  if (!wPx || !hPx) return undefined;

  let targetW = wPx;
  let targetH = hPx;

  // Match the previous Jimp downscaling rules.
  if (crop.aspect === 1 && targetH > 400) {
    targetW = targetW * (400 / targetH);
    targetH = 400;
  }
  if (targetH > 1080) {
    targetW = targetW * (1080 / targetH);
    targetH = 1080;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(targetW));
  canvas.height = Math.max(1, Math.round(targetH));

  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  ctx.drawImage(image, xPx, yPx, wPx, hPx, 0, 0, canvas.width, canvas.height);
  return canvasToJpegBlob(canvas, 0.7);
}

/** Re-encode the full image as a JPEG (kept so users can re-crop later). */
async function getOriginalImg(image: HTMLImageElement): Promise<Blob | undefined> {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  ctx.drawImage(image, 0, 0);
  return canvasToJpegBlob(canvas, 0.92);
}

const ImageDropperCropper: React.FC<IImageDropper> = (props) => {
  // these are all of the hooks for this module.
  const [thumbNail, setThumbNail] = useState(props.defaultThumbNail || '');
  const [upImg, setUpImg] = useState('');
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const busy = status === 'processing' || status === 'uploading';

  // Update thumbNail when defaultThumbNail prop changes
  useEffect(() => {
    if (props.defaultThumbNail !== undefined) {
      setThumbNail(props.defaultThumbNail || '');
    }
  }, [props.defaultThumbNail]);

  const [crop, setCrop] = useState<Crop>(
    props.aspect === 'defaultCrop'
      ? defaultCrop
      : props.aspect === 'squareCrop'
      ? squareCrop
      : props.aspect === 'spotlightCrop'
      ? spotlightCrop
      : spotlightLogoCrop
  );

  const [completedCrop, setCompletedCrop] = useState<Crop>(crop);
  const [cropStarted, setCropStarted] = useState(false);
  // max file size of 10mb
  const maxSize = 1048576 * 10;
  //************************ */

  const resetStatusSoon = useCallback(() => {
    window.setTimeout(() => {
      setStatus('idle');
      setStatusMsg('');
    }, 3000);
  }, []);

  const onSelectFile = (files: any) => {
    if (files && files.length > 0) {
      setStatus('idle');
      setStatusMsg('');
      setCropStarted(true);
      const reader: any = new FileReader();
      reader.addEventListener('load', () => setUpImg(reader.result));
      reader.readAsDataURL(files[0]);
    }
  };

  const onReject = (rejectedFiles: any) => {
    if (rejectedFiles.length > 1) {
      window.alert('Please select just one image file');
      return;
    }
    if (rejectedFiles[0].file.size > maxSize) {
      window.alert(`Your image file was too large`);
      return;
    }

    window.alert('Please select a valid image file');
  };

  const onLoad = useCallback(
    (img: HTMLImageElement) => {
      imgRef.current = img;

      // Reset to a valid centered crop for the freshly-loaded image. Without
      // this, react-image-crop v8 sometimes shows a crop box that overflows the
      // image bounds (especially when re-editing an existing photo).
      const fitted = centeredAspectCrop(img.width, img.height, crop.aspect);
      setCrop(fitted);
      setCompletedCrop(fitted);

      // We've set the crop ourselves, so tell ReactCrop not to override it.
      return false;
    },
    [crop.aspect],
  );

  async function handleCropSave() {
    if (busy) return;
    if (!imgRef.current) {
      setStatus('error');
      setStatusMsg('The image is still loading. Please try again in a moment.');
      return;
    }

    try {
      setStatus('processing');
      setStatusMsg('Preparing image…');

      const croppedImg = await getCroppedImg(imgRef.current, completedCrop);
      if (!croppedImg) {
        throw new Error(
          "We couldn't process this image. Please try a different photo (JPG or PNG).",
        );
      }

      // ALWAYS mint a fresh, unique key for every upload (new file or re-crop).
      // Never reuse / overwrite an existing S3 object: historical records share
      // placeholder image URLs, and overwriting one in place would change the
      // image for every record that points at it.
      const croppedImageFileName = props.fileName;

      // Re-upload the full-res original alongside it (as original_<newkey>) so a
      // later "Edit Image" can always find it. On Edit, imgRef already IS the
      // fetched original, so this just re-encodes what we have.
      const originalImage = await getOriginalImg(imgRef.current);

      setStatus('uploading');
      setStatusMsg('Uploading…');
      props.onUploadStatusChange?.(true);

      await Promise.resolve(
        props.onImageChange({
          croppedImage: croppedImg,
          croppedImageFileName,
          field: props.field,
          identifier: props.identifier ? props.identifier : 0,
          originalImage,
          originalImageFileName: `original_${croppedImageFileName}`,
        }),
      );

      setThumbNail(URL.createObjectURL(croppedImg));
      setStatus('success');
      setStatusMsg('Image uploaded');
      setCropStarted(false);
      resetStatusSoon();
    } catch (err) {
      console.error('Image upload failed', err);
      setStatus('error');
      setStatusMsg(
        (err as any)?.message ||
          'Upload failed. Please check your connection and try again.',
      );
    } finally {
      props.onUploadStatusChange?.(false);
    }
  }

  async function handleEditImage(event: any) {
    event.preventDefault();

    if (!props.defaultThumbNail) {
      return;
    }

    setStatus('processing');
    setStatusMsg('Loading image…');

    const candidates = [
      getOriginalFileURLFromCroppedURL(props.defaultThumbNail),
      props.defaultThumbNail,
    ].filter(Boolean);

    for (const url of candidates) {
      try {
        const resp = await fetch(url, { mode: 'cors' });
        if (!resp.ok) continue;
        const blob = await resp.blob();
        const dataUrl = await blobToDataURL(blob);
        setUpImg(dataUrl);
        setCropStarted(true);
        setStatus('idle');
        setStatusMsg('');
        return;
      } catch {
        // try the next candidate
      }
    }

    setStatus('error');
    setStatusMsg(
      "We couldn't open this image for editing. Try uploading a new one instead.",
    );
  }

  function handleChangeImage(event: any, open: () => void) {
    event.preventDefault();
    setStatus('idle');
    setStatusMsg('');
    open();
  }

  async function handleDeleteImage(event: any) {
    event.preventDefault();
    if (busy) return;

    try {
      setStatus('uploading');
      setStatusMsg('Removing image…');
      props.onUploadStatusChange?.(true);

      await Promise.resolve(
        props.onImageDelete({
          field: props.field,
          identifier: props.identifier ? props.identifier : 0,
          url: props.defaultThumbNail || '',
        }),
      );

      setThumbNail('');
      setStatus('success');
      setStatusMsg('Image removed');
      setCropStarted(false);
      resetStatusSoon();
    } catch (err) {
      console.error('Image delete failed', err);
      setStatus('error');
      setStatusMsg((err as any)?.message || 'Could not remove the image. Please try again.');
    } finally {
      props.onUploadStatusChange?.(false);
    }
  }

  return (
    <div className='npp-image-dropper-cropper'>
      <Dropzone
        accept='image/*'
        maxSize={maxSize}
        multiple={false}
        onDropAccepted={(acceptedFiles) => onSelectFile(acceptedFiles)}
        onDropRejected={(rejectedFiles) => onReject(rejectedFiles)}
      >
        {({ getRootProps, getInputProps, open }) => {
          return (
            <div {...getRootProps()}>
              <input {...getInputProps()} />

              {thumbNail === '' ? (
                <div className="npp-drop-zone-container">
                  <div className="plus-sign">
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-prefix="fas"
                      data-icon="plus"
                      className="svg-inline--fa fa-plus fa-w-14 fa-2x"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path
                        fill="#aaa0a0"
                        d="M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"
                      ></path>
                    </svg>
                  </div>
                </div>
              ) : (
                <div
                  className="npp-drop-zone-container"
                  style={{ backgroundImage: `url(${thumbNail})` }}
                >
                  {busy && (
                    <div className="npp-dropper-cropper-thumb-overlay">
                      <span className="npp-dropper-cropper-spinner" />
                    </div>
                  )}
                  {!!props.defaultThumbNail && (
                    <DropdownButton
                      id="dropdown-basic-button"
                      onClick={(e) => e.stopPropagation()}
                      className="npp-dropper-cropper-edit-dropdown"
                      title={<FontAwesomeIcon icon={faPen} />}
                    >
                      <Dropdown.Item as="button" onClick={handleEditImage}>
                        Edit Image
                      </Dropdown.Item>

                      <Dropdown.Item
                        as="button"
                        onClick={(e) => handleChangeImage(e, open)}
                      >
                        Change Image
                      </Dropdown.Item>

                      <Dropdown.Item as="button" onClick={handleDeleteImage}>
                        Delete Image
                      </Dropdown.Item>
                    </DropdownButton>
                  )}
                </div>
              )}
            </div>
          );
        }}
      </Dropzone>

      {status !== 'idle' && !cropStarted && (
        <div
          className={`npp-dropper-cropper-status npp-dropper-cropper-status--${status}`}
          role="status"
        >
          {busy && <span className="npp-dropper-cropper-spinner" />}
          <span>{statusMsg}</span>
        </div>
      )}

      <div
        style={{ display: cropStarted ? 'flex' : 'none' }}
        className='npp-modal-cropper-wrapper'
      >
        <div className='npp-modal-cropper-title'>
          Drag to Reposition the Crop
        </div>
        <ReactCrop
          className='npp-modal-cropper'
          src={upImg}
          onImageLoaded={onLoad}
          crop={crop as any}
          onChange={(pix: any, c: any) => setCrop(c)}
          onComplete={(pix: any, c: any) => setCompletedCrop(c)}
        />

        {status !== 'idle' && (
          <div
            className={`npp-dropper-cropper-status npp-dropper-cropper-status--${status}`}
            role="status"
          >
            {busy && <span className="npp-dropper-cropper-spinner" />}
            <span>{statusMsg}</span>
          </div>
        )}

        <div className='npp-dropper-cropper-buttons'>
          <div className='save-button'>
            <NeddieButton
              onClick={(e: any) => {
                e.preventDefault();
                handleCropSave();
              }}
              value={busy ? 'Saving…' : 'Save'}
              isSquare={true}
              type={busy ? 'disabled' : 'button'}
            ></NeddieButton>
          </div>
          <div>
            {' '}
            <NeddieButton
              onClick={(e: any) => {
                // NeddieButton never sets a native `type`, so inside a <form>
                // (e.g. the donor fundraiser form) it submits on click unless
                // we preventDefault ourselves — keep this from our branch.
                e.preventDefault();
                if (busy) return;
                setCropStarted(false);
                setStatus('idle');
                setStatusMsg('');
              }}
              value='Cancel'
              isSquare={true}
              isLight={true}
              type={busy ? 'disabled' : 'button'}
            ></NeddieButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageDropperCropper;
